import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Privacy Policy, Forsyt"
        description="How Forsyt collects, processes, and protects personal data. GDPR-compliant data subject rights, lawful basis, sub-processors, retention, and how to contact our DPO."
        path="/privacy"
      />

      <section className="pt-20 pb-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
            <Shield className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">Privacy Policy</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight tracking-tight">
            How we collect, use, and protect personal data
          </h1>
          <p className="text-sm text-gray-500">
            <strong>Effective:</strong> February 2026 &middot;{' '}
            <strong>Version:</strong> 2.0 &middot;{' '}
            <Link to="/security" className="text-blue-600 hover:underline">See full security &amp; trust documentation</Link>
          </p>
          <div className="inline-flex items-center gap-2 px-3 py-1 mt-4 rounded-full bg-emerald-50 border border-emerald-100" data-testid="privacy-iso-chip">
            <Shield className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">ISO 27001 Certified &middot; GDPR Compliant</span>
          </div>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <article className="max-w-3xl mx-auto px-6 [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:tracking-tight [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:text-sm [&_p]:md:text-[15px] [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1.5 [&_li]:text-sm [&_li]:md:text-[15px] [&_li]:text-gray-700 [&_li]:leading-relaxed [&_a]:text-blue-600 [&_a]:hover:underline [&_strong]:text-gray-900 [&_hr]:my-8 [&_hr]:border-gray-200">
          <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
            This Privacy Policy explains how <strong>Credos Forsyt Private Limited</strong> (operating
            under the brand &ldquo;Forsyt&rdquo;; &ldquo;we&rdquo;, &ldquo;us&rdquo;) collects, uses, and
            protects personal data when you use our marketing site (forsyt.com), our Revenue OS
            application, and any related services. Our registered office is in New Delhi, India.
          </p>
          <p>
            We act in two capacities: as a <strong>data controller</strong> for marketing-site
            visitors and lead-form submissions; and as a <strong>data processor</strong> for the
            customer pipeline data we process on behalf of paying customers. The EU General Data
            Protection Regulation (GDPR), the UK Data Protection Act 2018, and the Indian Digital
            Personal Data Protection Act, 2023 (DPDPA) apply where relevant.
          </p>

          <h2>1. Who we are &amp; how to reach us</h2>
          <ul>
            <li><strong>Legal entity:</strong> Credos Forsyt Private Limited (operating as &ldquo;Forsyt&rdquo;)</li>
            <li><strong>Registered office:</strong> New Delhi, India</li>
            <li><strong>Founder &amp; Data Protection Officer:</strong> Udit Pandoh</li>
            <li><strong>Privacy, DPO &amp; security disclosures:</strong> <a href="mailto:privacy@forsyt.com">privacy@forsyt.com</a></li>
            <li><strong>Commercial &amp; billing:</strong> <a href="mailto:legal@forsyt.com">legal@forsyt.com</a></li>
            <li><strong>Governing law:</strong> India, with exclusive jurisdiction of the courts of New Delhi.</li>
          </ul>

          <h2>2. What personal data we collect</h2>
          <h3>As a controller (marketing site):</h3>
          <ul>
            <li>Email address (lead-form submissions, newsletter)</li>
            <li>Name and company (if provided)</li>
            <li>Marketing-site analytics: pseudonymized page-view events, only after explicit cookie consent</li>
            <li>Communication metadata (when you email us)</li>
          </ul>
          <h3>As a processor (Revenue OS application):</h3>
          <ul>
            <li>Names, business email, business phone, job title of your prospects and customers</li>
            <li>CRM activity events (deal stages, close dates, notes)</li>
            <li>Call transcripts and email content where shared with the application</li>
            <li>Application user accounts (your team members) and access logs</li>
          </ul>
          <p>
            We do not solicit or process special category data (health, religion, ethnicity,
            political views, etc.). If such data appears incidentally in pipeline content, you
            (the controller) are responsible for instructing us on its handling.
          </p>

          <h2>3. Lawful basis for processing</h2>
          <ul>
            <li><strong>Performance of a contract</strong> (GDPR Art. 6(1)(b)): for processing pipeline data to deliver the Revenue OS service to paying customers.</li>
            <li><strong>Legitimate interest</strong> (GDPR Art. 6(1)(f)): for security, abuse prevention, fraud detection, and product analytics. Balancing test available on request.</li>
            <li><strong>Consent</strong> (GDPR Art. 6(1)(a)): for marketing emails, non-essential cookies, and product analytics on the marketing site. You can withdraw consent at any time.</li>
            <li><strong>Legal obligation</strong> (GDPR Art. 6(1)(c)): for tax, accounting, and law-enforcement disclosure where compelled.</li>
          </ul>

          <h2>4. How we use personal data</h2>
          <ul>
            <li>To provide and operate the Revenue OS service</li>
            <li>To respond to enquiries, demos, and support requests</li>
            <li>To improve our service through pseudonymized product analytics</li>
            <li>To send transactional and (with consent) marketing communications</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h2>5. Where data is stored &amp; hosted</h2>
          <ul>
            <li><strong>EU / UK customers:</strong> AWS Frankfurt (eu-central-1)</li>
            <li><strong>Indian / APAC customers:</strong> AWS Mumbai (ap-south-1)</li>
            <li><strong>North American customers:</strong> AWS Virginia (us-east-1) on request</li>
          </ul>
          <p>
            For EU/UK customers, data does not leave the EEA by default. Any incidental transfer
            (e.g., engineer access from outside EEA for support) is covered by Standard
            Contractual Clauses (SCCs) and supplementary technical measures (encryption at rest
            and in transit, role-based access).
          </p>

          <h2>6. Sub-processors</h2>
          <p>
            We engage third-party sub-processors to deliver the service. The complete list is
            published at <Link to="/sub-processors">/sub-processors</Link>, including each
            vendor&rsquo;s purpose, the data they access, hosting region, and DPA reference. We
            give 30 calendar days&rsquo; advance notice via email and in-app banner before any
            change to this list.
          </p>

          <h2>7. Your rights as a data subject</h2>
          <p>Wherever applicable law grants you the following rights, we honour them:</p>
          <ul>
            <li><strong>Right of access</strong> (GDPR Art. 15)</li>
            <li><strong>Right to rectification</strong> (Art. 16)</li>
            <li><strong>Right to erasure</strong> &mdash; &ldquo;right to be forgotten&rdquo; (Art. 17)</li>
            <li><strong>Right to restrict processing</strong> (Art. 18)</li>
            <li><strong>Right to data portability</strong> (Art. 20)</li>
            <li><strong>Right to object to processing</strong> (Art. 21)</li>
            <li><strong>Right not to be subject to automated decisions</strong> (Art. 22)</li>
            <li><strong>Right to lodge a complaint</strong> with your national supervisory authority (Art. 77)</li>
          </ul>
          <p>
            To exercise any of these rights, email <a href="mailto:privacy@forsyt.com">privacy@forsyt.com</a>.
            We acknowledge requests within 5 business days and resolve them within the GDPR&rsquo;s
            statutory one-month window. Complex requests may extend up to two further months
            with notice. If you are unhappy with our response, you may lodge a complaint with your
            national or regional supervisory authority (for EEA residents) or the Information
            Commissioner&rsquo;s Office (for UK residents).
          </p>

          <h2>8. Retention</h2>
          <ul>
            <li><strong>Customer pipeline data:</strong> retained for the duration of the contract.</li>
            <li><strong>AI conversation context:</strong> in-memory only &mdash; discarded at session end.</li>
            <li><strong>Application logs:</strong> retained for as long as operationally needed and then purged.</li>
            <li><strong>Marketing-site lead data:</strong> 3 years from last engagement, or until consent withdrawn.</li>
            <li><strong>On termination:</strong> all customer data deleted from active systems within 30 days. A signed deletion certificate is provided on request.</li>
          </ul>

          <h2>9. Security</h2>
          <ul>
            <li>AES-256 encryption at rest; TLS 1.3 in transit</li>
            <li>Role-based access control for all personnel with access to production systems</li>
            <li>Least-privilege production access with periodic access reviews</li>
            <li>Audit logging of all access to personal data</li>
            <li>Continuous backups with point-in-time restore</li>
            <li>ISO/IEC 27001:2022 certified Information Security Management System</li>
          </ul>
          <p>
            See <Link to="/security">/security</Link> for our complete security and trust
            documentation.
          </p>

          <h2>10. Personal data breaches</h2>
          <p>
            In the event of a confirmed personal-data breach, we notify affected customers and
            relevant supervisory authorities within <strong>72 hours</strong> per GDPR Art. 33.
            We maintain a documented incident-response process and review it as our operations
            mature.
          </p>

          <h2>11. AI &amp; automated decision-making</h2>
          <p>
            Forsyt uses Anthropic Claude (Sonnet) for AI Deal Chat reasoning. Anthropic&rsquo;s
            commercial terms guarantee zero training on customer prompts or responses. Our
            backend processes prompts in-memory only &mdash; nothing is persisted to Forsyt
            servers after the response is returned.
          </p>
          <p>
            Decisions that materially affect a deal (e.g., close-date push, status change) are
            always reviewed by a human before being written back to your CRM. You have the
            right not to be subject to a decision based solely on automated processing (GDPR
            Art. 22).
          </p>

          <h2>12. Cookies &amp; tracking</h2>
          <p>
            Our marketing site uses essential cookies (always on) and optional analytics
            cookies (Google Analytics, PostHog). Optional cookies fire <strong>only after
            you give explicit consent</strong> via the cookie banner. You can withdraw consent
            at any time by clearing your browser&rsquo;s cookies for this site, which re-displays
            the consent banner.
          </p>

          <h2>13. International transfers</h2>
          <p>
            Where personal data of EEA / UK / Swiss data subjects is transferred outside those
            jurisdictions (e.g., to a US-based sub-processor), we rely on Standard Contractual
            Clauses (SCCs) and, where applicable, the UK International Data Transfer Addendum.
            Supplementary technical measures (encryption, access controls, pseudonymization)
            apply.
          </p>

          <h2>14. Children</h2>
          <p>
            Forsyt is a B2B service and is not directed at children. Consistent with India&rsquo;s
            Digital Personal Data Protection Act, 2023, we treat any individual under 18 as a
            child and do not knowingly collect personal data from them. If we become aware that
            we have done so, we delete it immediately.
          </p>

          <h2>15. Changes to this policy</h2>
          <p>
            We update this policy from time to time. Material changes are notified by email
            (to customer admins and newsletter subscribers) and via an in-app banner at least
            30 days before they take effect. The version history is preserved &mdash; previous
            versions are available on request.
          </p>

          <h2>16. Contact</h2>
          <p>For any privacy, DPO, GDPR, or security-disclosure question, write to{' '}
            <a href="mailto:privacy@forsyt.com">privacy@forsyt.com</a>. For commercial or
            billing matters, write to <a href="mailto:legal@forsyt.com">legal@forsyt.com</a>.
          </p>

          <hr />

          <p className="text-sm text-gray-500">
            <Mail className="w-4 h-4 inline mr-1 text-blue-600" /> If you cannot resolve a
            privacy concern with us, you may lodge a complaint with your national supervisory
            authority for data protection.
          </p>
        </article>

        <div className="max-w-3xl mx-auto px-6 mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/security"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-md hover:shadow-lg transition-all"
            data-testid="privacy-cta-security"
          >
            See full security &amp; trust documentation
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/sub-processors"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border border-gray-300 hover:border-blue-600 bg-white rounded-full transition-all"
            data-testid="privacy-cta-subprocessors"
          >
            View sub-processors
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
